import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { getMediaUrl } from '@/utils/url-utils'
import { Plus, Loader2 } from "lucide-react"
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from 'sonner'

import { useBlog } from '@/hooks/use-blog'
import { useBlogCategories } from '@/hooks/use-blog-categories'
import { useBlogTags } from '@/hooks/use-blog-tags'
import { PostList } from './components/post-list'
import { CategoryList } from './components/category-list'
import { TagList } from './components/tag-list'
import { BlogPostForm } from '@/modules/blog/components/post-form'
import { BlogPost, blogService } from '@/services/other-services'

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
]

export default function Blog() {
  const { data: blogData, isLoading: isBlogLoading, createPost, updatePost, deletePost } = useBlog()
  const { data: categories } = useBlogCategories()
  const { data: tags } = useBlogTags()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [isTranslating, setIsTranslating] = useState(false)
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null)
  const [previewPost, setPreviewPost] = useState<BlogPost | null>(null)

  const posts = blogData?.items || []

  const handleCreate = () => {
    setEditingPost(null)
    setIsFormOpen(true)
  }

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post)
    setIsFormOpen(true)
  }
  const handleView = (post: BlogPost) => {
    setPreviewPost(post)
    setIsPreviewOpen(true)
  }

  const handleSubmit = async (data: Partial<BlogPost>) => {
    try {
      if (editingPost) {
        await updatePost({ id: editingPost.id, data })
      } else {
        await createPost(data)
      }
      setIsFormOpen(false)
    } catch (error) {
      console.error('Submit error:', error)
      toast.error('İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin.')
    }
  }

  const handleBulkTranslate = async () => {
    if (!confirm('Tüm blog yazıları için eksik çeviriler AI ile tamamlanacak. Bu işlem yazı sayısına göre vakit alabilir. Emin misiniz?')) return

    setIsTranslating(true)
    try {
      const res = await blogService.bulkTranslate()
      toast.success(res.message || 'Toplu çeviri başarıyla tamamlandı')
      window.location.reload()
    } catch (error) {
      console.error('Bulk translate error:', error)
      toast.error('Toplu çeviri sırasında bir hata oluştu')
    } finally {
      setIsTranslating(false)
    }
  }

  if (isBlogLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[60vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Blog Yönetimi</h1>
            <p className="text-sm text-muted-foreground">Blog yazılarını, kategorilerini ve etiketlerini yönetin</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Yeni Yazı
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Toplam Yazı</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{blogData?.total || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Yayında</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{posts.filter(p => p.isPublished).length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Kategoriler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{categories?.length || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Etiketler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{tags?.length || 0}</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="posts" className="space-y-4">
          <TabsList>
            <TabsTrigger value="posts">Yazılar</TabsTrigger>
            <TabsTrigger value="categories">Kategoriler</TabsTrigger>
            <TabsTrigger value="tags">Etiketler</TabsTrigger>
          </TabsList>

          <TabsContent value="posts" className="space-y-4">
            <PostList
              posts={posts}
              isLoading={isBlogLoading}
              onEdit={handleEdit}
              onDelete={deletePost}
              onView={handleView}
              onBulkTranslate={handleBulkTranslate}
              isTranslating={isTranslating}
            />
          </TabsContent>

          <TabsContent value="categories">
            <CategoryList />
          </TabsContent>

          <TabsContent value="tags">
            <TagList />
          </TabsContent>
        </Tabs>

        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{editingPost ? 'Yazıyı Düzenle' : 'Yeni Yazı Ekle'}</DialogTitle>
            </DialogHeader>
            {isFormOpen && (
              <BlogPostForm
                post={editingPost}
                onSubmit={handleSubmit}
                onCancel={() => setIsFormOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Önizleme: {previewPost && (typeof previewPost.title === 'object' ? previewPost.title.tr : previewPost.title)}</DialogTitle>
            </DialogHeader>
            {previewPost && (
              <div className="space-y-6 py-4">
                {previewPost.featuredImage && (
                  <div className="aspect-video w-full overflow-hidden rounded-lg border">
                    <img
                      src={getMediaUrl(previewPost.featuredImage)}
                      alt="Kapak Görseli"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground italic">
                  <span>Yazar: {previewPost.authorId?.name || 'Bilinmiyor'}</span>
                  <span>•</span>
                  <span>Tarih: {new Date(previewPost.createdAt).toLocaleDateString('tr-TR')}</span>
                  <span>•</span>
                  <span>Okunma: {previewPost.readingTime || 0} dk</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Array.isArray(previewPost.categories) && previewPost.categories.map((cat: any) => (
                    <Badge key={typeof cat === 'object' ? cat._id : cat} variant="outline">
                      {typeof cat === 'object' ? cat.name?.tr : cat}
                    </Badge>
                  ))}
                </div>
                <div
                  className="blog-content prose prose-sm dark:prose-invert max-w-none border-t pt-6"
                  dangerouslySetInnerHTML={{
                    __html: typeof previewPost.content === 'object' ? previewPost.content.tr : previewPost.content
                  }}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </Main>
    </>
  )
}
